// cspell:words: undock
import React, { createContext, useContext, useMemo, useState } from 'react';

import { AgentPortal } from '../components/agent/agent-portal';
import { useSlides } from './use-candidate-slides';
import { useLocalStorage } from './use-local-storage';
import { usePageDialog } from './use-page-dialog';

export interface AgentContextValue {
    open: boolean;
    dockSide: 'left' | 'right';
    isDocked: boolean;
    position: { x: number; y: number };
    hasActiveChat: boolean;
    conversationId: string | null;
    width: number;
    prefilledMessage: string | null;
    onOpen: () => void;
    onClose: () => void;
    onToggle: () => void;
    onToggleDockSide: () => void;
    onUndock: (position: { x: number; y: number }) => void;
    onUpdatePosition: (position: { x: number; y: number }) => void;
    onNewChat: () => void;
    onChatStarted: () => void;
    onSelectConversation: (id: string) => void;
    onSetConversationId: (id: string | null) => void;
    onSetWidth: (width: number) => void;
    onOpenWithMessage: (message: string, conversationId?: string) => void;
    onClearPrefilledMessage: () => void;
}

export const AgentContext = createContext<AgentContextValue>(undefined);

interface AgentProviderProps {
    children?: React.ReactNode;
}

const DEFAULT_SIDEBAR_WIDTH = 520;
const DEFAULT_SIDEBAR_HEIGHT = 600;
const TOP_SPACING = 80;
const SIDE_SPACING = 24;

const getDefaultPosition = () => {
    // Center the sidebar while respecting margins
    const centerX = (window.innerWidth - DEFAULT_SIDEBAR_WIDTH) / 2;
    const centerY = (window.innerHeight - DEFAULT_SIDEBAR_HEIGHT) / 2;

    // Constrain to honor margins on all sides
    const minX = SIDE_SPACING;
    const minY = TOP_SPACING;
    const maxX = window.innerWidth - DEFAULT_SIDEBAR_WIDTH - SIDE_SPACING;
    const maxY = window.innerHeight - DEFAULT_SIDEBAR_HEIGHT - SIDE_SPACING;

    return {
        x: Math.max(minX, Math.min(centerX, maxX)),
        y: Math.max(minY, Math.min(centerY, maxY))
    };
};

const AgentProvider = ({ children }: AgentProviderProps): JSX.Element => {
    const [dockSide, setDockSide] = useLocalStorage<'left' | 'right'>('agent-sidebar-dock-side', 'right');
    const [isDocked, setIsDocked] = useLocalStorage('agent-sidebar-is-docked', true);
    const [position, setPosition] = useLocalStorage('agent-sidebar-position', getDefaultPosition());
    const [width, setWidth] = useLocalStorage('agent-sidebar-width', DEFAULT_SIDEBAR_WIDTH);
    const [open, setOpen] = useState(false);
    const [hasActiveChat, setHasActiveChat] = useState(false);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [prefilledMessage, setPendingMessage] = useState<string | null>(null);
    const { registerAgentContext: registerAgentContextForPageDialog } = usePageDialog();
    const { registerAgentContext: registerAgentContextForSlides } = useSlides();

    React.useEffect(() => {
        const handleKeyboardShortcut = (event: KeyboardEvent) => {
            if (event.key === '/' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                setOpen((prev) => !prev);
            }
            if (event.key === '.' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                setDockSide((prev) => (prev === 'right' ? 'left' : 'right'));
                setIsDocked(true);
            }
        };

        document.addEventListener('keydown', handleKeyboardShortcut);

        return () => {
            document.removeEventListener('keydown', handleKeyboardShortcut);
        };
    }, [setDockSide, setIsDocked]);

    const onOpen = React.useCallback(() => setOpen(true), []);
    const onClose = React.useCallback(() => setOpen(false), []);
    const onToggle = React.useCallback(() => setOpen((prev) => !prev), []);
    const onToggleDockSide = React.useCallback(() => {
        setDockSide((prev) => (prev === 'right' ? 'left' : 'right'));
        setIsDocked(true);
    }, [setDockSide, setIsDocked]);
    const onUndock = React.useCallback(
        (newPosition: { x: number; y: number }) => {
            setIsDocked(false);
            setPosition(newPosition);
        },
        [setIsDocked, setPosition]
    );
    const onUpdatePosition = React.useCallback(
        (newPosition: { x: number; y: number }) => {
            setPosition(newPosition);
        },
        [setPosition]
    );
    const onNewChat = React.useCallback(() => {
        setHasActiveChat(false);
        setConversationId(null);
        // Analytics can be added here when needed
    }, []);
    const onChatStarted = React.useCallback(() => setHasActiveChat(true), []);
    const onSelectConversation = React.useCallback((id: string) => {
        setConversationId(id);
    }, []);
    const onSetConversationId = React.useCallback((id: string | null) => {
        setConversationId(id);
    }, []);

    const onSetWidth = React.useCallback(
        (newWidth: number) => {
            setWidth(newWidth);
        },
        [setWidth]
    );

    const onOpenWithMessage = React.useCallback((message: string, cid?: string) => {
        setConversationId(cid ?? null);
        setHasActiveChat(false);
        setPendingMessage(message);
        setOpen(true);
    }, []);

    const onClearPrefilledMessage = React.useCallback(() => {
        setPendingMessage(null);
    }, []);

    const contextValue = useMemo<AgentContextValue>(
        () => ({
            conversationId,
            dockSide,
            hasActiveChat,
            isDocked,
            onChatStarted,
            onClearPrefilledMessage,
            onClose,
            onNewChat,
            onOpen,
            onOpenWithMessage,
            onSelectConversation,
            onSetConversationId,
            onSetWidth,
            onToggle,
            onToggleDockSide,
            onUndock,
            onUpdatePosition,
            open,
            position,
            prefilledMessage,
            width
        }),
        [
            conversationId,
            dockSide,
            hasActiveChat,
            isDocked,
            onChatStarted,
            onClearPrefilledMessage,
            onClose,
            onNewChat,
            onOpen,
            onOpenWithMessage,
            onSelectConversation,
            onSetConversationId,
            onSetWidth,
            onToggle,
            onToggleDockSide,
            onUndock,
            onUpdatePosition,
            open,
            prefilledMessage,
            position,
            width
        ]
    );

    React.useEffect(() => {
        registerAgentContextForPageDialog(contextValue);
        registerAgentContextForSlides(contextValue);
    }, [contextValue, registerAgentContextForPageDialog, registerAgentContextForSlides]);

    return (
        <AgentContext.Provider value={contextValue}>
            {children}
            <AgentPortal />
        </AgentContext.Provider>
    );
};

const useAgent = () => useContext(AgentContext);

export { AgentProvider, useAgent };
